import { useEffect, useState } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { clsx } from 'clsx';
import { BookOpenIcon } from '@heroicons/react/outline';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import { MenuTransition } from '@mosey/components/menus/common/MenuTransition';
import { DropdownMenu, MenuItem } from '@mosey/components/menus/DropdownMenu';
import { NavItem } from '@mosey/components/navigation/NavItem';
import { SubNavItem } from '@mosey/components/navigation/SubNavItem';
import { Search } from './Search';
import {
  OfficeIcon,
  CollapseLeftIcon,
  ExpandRightIcon,
  SupportIcon,
  UserIcon,
  HomeIcon,
  CalendarIcon,
  TaskListIcon,
  SettingsIcon,
  LocationIcon,
  RadarIcon,
  EmailIcon,
} from '@mosey/components/Icons';
import * as config from '../../settings/config';
import logo from '../../assets/logo.svg';
import logomark from '../../assets/logomark.svg';
import { useUser } from '../../hooks/useUser';
import { ImpersonationAlert } from './ImpersonationAlert';
import { useTasks } from '../../views/tasks-framework/utils/hooks';

type AppNavProps = {
  children?: React.ReactNode;
};

const AppNav = ({ children }: AppNavProps) => {
  const user = useUser();
  const isHome = useMatch('/home');
  const isLocationResolver = Boolean(
    useMatch('locations/:countryId/:locationId/resolver/:resolverType?/*'),
  );
  const isGenericResolver = Boolean(useMatch('resolver/:resolverType?/*'));
  const tasks = useTasks();
  const [isNavClosed, setIsNavClosed] = useState(!isHome);
  const {
    legal_entities: legalEntities,
    legal_entity: {
      business_profile_submitted: isBusinessProfileSubmitted,
      is_handbook_enabled: isHandbookEnabled,
    },
  } = user;

  useEffect(() => {
    setIsNavClosed(
      !isHome ||
        ((isLocationResolver || isGenericResolver) && tasks.length > 1),
    );
  }, [isHome, isLocationResolver, isGenericResolver, tasks.length]);

  const isNavDisabled = !isBusinessProfileSubmitted;
  const navItemDisabledMessage =
    'Finish setting up your business profile to unlock.';

  const OpenCloseIcon = isNavClosed ? ExpandRightIcon : CollapseLeftIcon;

  return (
    <div className="flex min-h-screen">
      <aside
        className={clsx(
          'relative hidden h-screen border-r bg-white transition-nav duration-300 ease-in-out sm:block',
          {
            'w-16': isNavClosed,
            'w-72': !isNavClosed,
          },
        )}
      >
        <nav>
          <div
            className={clsx(
              'flex h-16 items-center border-b border-gray-200 transition-nav duration-300',
              {
                'px-5': isNavClosed,
                'px-7': !isNavClosed,
              },
            )}
          >
            <div
              className={clsx(
                'flex shrink-0 transition-nav duration-300 ease-in-out',
                {
                  'w-6 opacity-1': isNavClosed,
                  'opacity-0 w-0': !isNavClosed,
                },
              )}
            >
              <img className="mx-auto size-6" src={logomark} alt="Logo" />
            </div>

            <div
              className={clsx(
                'overflow-hidden transition-nav duration-300 ease-in-out',
                {
                  'opacity-0 w-0 px-0': isNavClosed,
                  'w-20': !isNavClosed,
                },
              )}
            >
              <img className="mt-1" src={logo} alt="Mosey" />
            </div>
          </div>

          <button
            className="absolute right-0 top-16 z-50 flex size-5 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-2xl border-2 border-gray-200 bg-white"
            onClick={() => {
              setIsNavClosed(!isNavClosed);
            }}
            aria-label={`${isNavClosed ? 'Open' : 'Close'} navigation menu`}
          >
            <OpenCloseIcon
              className={clsx(
                'size-full',
                isNavClosed ? 'text-rose-500' : 'text-rose-700',
              )}
            />
          </button>

          <Menu
            as="div"
            className={clsx(
              'relative mb-2 mr-4 mt-8 transition-nav duration-300 ease-in-out',
              isNavClosed ? 'ml-3' : 'ml-5',
            )}
          >
            <MenuButton
              disabled={isNavClosed || legalEntities.length < 2}
              className="flex items-center gap-x-2 p-2 font-semibold text-gray-700"
            >
              <OfficeIcon className="size-6 shrink-0 text-gray-400" />
              <div
                className={clsx(
                  'truncate text-left transition-nav duration-300 ease-in-out',
                  isNavClosed ? 'w-0 opacity-0' : 'w-[155px]',
                )}
              >
                {user.legal_entity.name}
              </div>
            </MenuButton>

            <MenuTransition>
              <MenuItems className="absolute z-10 mt-3 max-h-60 w-full origin-top-right overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                {legalEntities.map((legalEntity) => (
                  <MenuItem
                    as={Link}
                    key={legalEntity.id}
                    to={`/home?legal_entity_id=${legalEntity.id}`}
                    selected={legalEntity.id === user.legal_entity.id}
                  >
                    {legalEntity.name}
                  </MenuItem>
                ))}
              </MenuItems>
            </MenuTransition>
          </Menu>

          <div className="mx-6 mb-4 h-px py-2">
            <div className="border-t border-gray-100" />
          </div>
          <div>
            <div
              className={clsx(
                'flex flex-col font-normal text-gray-600 transition-nav duration-300',
                {
                  'px-5 ml-0.5': isNavClosed,
                  'px-7': !isNavClosed,
                },
              )}
            >
              <NavItem
                name="Home"
                to="/home"
                Icon={HomeIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              />
              <NavItem
                name="Locations"
                to="/locations/usa"
                Icon={LocationIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              />
              <NavItem
                name="Tasks"
                to="/tasks"
                Icon={TaskListIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              >
                <SubNavItem name="Assigned to me" to="/tasks/me" />
                <SubNavItem name="All" to="/tasks/all" />
              </NavItem>
              <NavItem
                name="Calendar"
                to="/calendar"
                Icon={CalendarIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              />
              <NavItem
                name="Legislation"
                to="/legislation"
                Icon={RadarIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              />
              <NavItem
                name="Mailroom"
                to="/mail"
                Icon={EmailIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              />
              {isHandbookEnabled && (
                <NavItem
                  name="Handbook"
                  to="/handbook"
                  Icon={BookOpenIcon}
                  isClosed={isNavClosed}
                  isDisabled={isNavDisabled}
                  disabledTooltipMessage={navItemDisabledMessage}
                />
              )}

              <div className="mx-6 mb-4 mt-2 h-px py-2">
                <div className="border-t border-gray-100" />
              </div>
              <NavItem
                name="Settings"
                to="/settings"
                Icon={SettingsIcon}
                isClosed={isNavClosed}
                isDisabled={isNavDisabled}
                disabledTooltipMessage={navItemDisabledMessage}
              />
            </div>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://support.mosey.com"
              className={clsx(
                'absolute bottom-0 flex h-12 w-full items-center border-t bg-gray-100 text-xs font-normal text-gray-500 transition-nav duration-300',
                {
                  'px-5': isNavClosed,
                  'pl-8': !isNavClosed,
                },
              )}
            >
              <SupportIcon className="mx-auto size-5 shrink-0" />

              <div
                className={clsx(
                  'static h-4 overflow-hidden transition-nav duration-300 ease-in-out',
                  {
                    'opacity-0 w-0': isNavClosed,
                    'w-72 ml-2': !isNavClosed,
                  },
                )}
              >
                Help »
              </div>
            </a>
          </div>
        </nav>
      </aside>
      <div className="flex h-screen w-full flex-col overflow-y-hidden bg-white">
        <header className="z-40 hidden h-16 w-full border-b border-gray-200 bg-white py-2 pr-6 sm:flex sm:items-center sm:justify-end">
          {!isNavDisabled && (
            <div className="flex-1">
              <div className="mx-auto w-96">
                <Search />
              </div>
            </div>
          )}

          <DropdownMenu
            isIconButton
            ariaButtonText="Open user menu"
            buttonContent={
              <UserIcon className="hidden size-8 rounded-full border p-1 text-gray-500 sm:block" />
            }
          >
            {user.role === 'admin' && (
              <MenuItem as={Link} to={config.ADMIN_BASE_URL} target="_blank">
                Admin
              </MenuItem>
            )}
            <MenuItem as={Link} to="/logout">
              Sign Out
            </MenuItem>
          </DropdownMenu>
        </header>

        <ImpersonationAlert />

        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
};

export default AppNav;
